import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import IconButton from 'components/common/IconButton';
import PageHeader from 'components/common/PageHeader';
import FalconComponentCard from 'components/common/FalconComponentCard';
import FileUpload from 'components/doc-components/FileUpload';

const EditVacancy = () => {
  const navigate = useNavigate();
  const [fileUrl, setFileUrl] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    closingDate: '',
    reference: '',
    contact: '',
    tel: '',
    email: '',
    content: '',
    status: 'False',
    downloadTitle: '',
    downloadFile: '',
    closingDateDD: '',
    closingDateMM: '',
    closingDateYYYY: '',
    date_added: '',
    user_id: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setFormData(prevData => ({
          ...prevData,
          user_id: uid,
        }));
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const id = window.location.pathname.split('/').pop();

      if (id) {
        try {
          const apiUrl = `${process.env.REACT_APP_API_URL}`;

          const response = await axios.get(`${apiUrl}/vacancies/id/${id}`);

          if (response.data) {
            console.log('Full API response:', response.data);
            
            // Handle different possible response structures
            const vacancyData = response.data.data ? response.data.data[0] : 
                               Array.isArray(response.data) ? response.data[0] : 
                               response.data;
            
            console.log('vacancyData fetched:', vacancyData);
            
            if (!vacancyData) {
              console.error('No vacancy data found in response');
              return;
            }
            
            // Parse closing_date from DD/MM/YYYY format
            const closingDateParts = vacancyData.closing_date ? vacancyData.closing_date.split('/') : ['', '', ''];

            setFormData(prevData => ({
              ...prevData,
              title: vacancyData.title || '',
              closingDate: vacancyData.closing_date || '',
              reference: vacancyData.reference || '',
              contact: vacancyData.contact || '',
              tel: vacancyData.tel || '',
              email: vacancyData.email || '',
              content: vacancyData.content || '',
              status: vacancyData.status || 'False',
              downloadTitle: vacancyData.download_title || '',
              downloadFile: vacancyData.download_file || '',
              closingDateDD: closingDateParts[0] || '',
              closingDateMM: closingDateParts[1] || '',
              closingDateYYYY: closingDateParts[2] || '',
              date_added: vacancyData.date_added || '',
            }));
            
            setFileUrl(vacancyData.download_file || null);
            console.log('Form data updated with:', vacancyData.title);
          }
        } catch (err) {
          console.log('Error fetching vacancy:', err);
        }
      } else {
        navigate('/vacancies/list');
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form data', formData);

    const date = `${formData.closingDateDD}/${formData.closingDateMM}/${formData.closingDateYYYY}`;

    const { closingDateDD, closingDateMM, closingDateYYYY } = formData;
    const inputDate = new Date(closingDateYYYY, closingDateMM - 1, closingDateDD);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    try {
      if (inputDate < today || closingDateYYYY < today.getFullYear() || closingDateMM > 12 || closingDateDD > 31) {
        toast.error('Closing date is invalid.');
        return;
      }
    } catch (error) {
      toast.error('Closing date is invalid.');
      return;
    }

    try {
      let formData2 = new FormData();
      const downloadFileValue = fileUrl || formData.downloadFile || '';
      
      formData2.append('title', formData.title || '');
      formData2.append('date_updated', moment.utc().format('YYYY-MM-DD HH:mm'));
      formData2.append('date_added', formData.date_added || '');
      formData2.append('closing_date', date);
      formData2.append('reference', formData.reference || '');
      formData2.append('contact', formData.contact || '');
      formData2.append('tel', formData.tel || '');
      formData2.append('email', formData.email || '');
      formData2.append('content', formData.content || '');
      formData2.append('status', formData.status || 'False');
      formData2.append('download_title', formData.downloadTitle || '');
      formData2.append('fileUrl', downloadFileValue);
      formData2.append('download_file', downloadFileValue);
      formData2.append('user_id', formData.user_id || '');

      console.log('Sending update with data:', {
        title: formData.title,
        closing_date: date,
        reference: formData.reference,
        contact: formData.contact,
        status: formData.status,
        download_file: downloadFileValue
      });

      const apiUrl = `${process.env.REACT_APP_API_URL}`;
      const id = window.location.pathname.split('/').pop();

      const response = await axios.put(`${apiUrl}/vacancies/id/${id}`, formData2);

      console.log('Update response:', response.data);

      if (response) {
        console.log('responded', response.statusText);
        toast.success(`Vacancy updated successfully`, {
          theme: 'colored',
        });
        navigate('/vacancies/list');
      }
    } catch (err) {
      console.log('Error', err.message);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === 'title') {
      setFormData({
        ...formData,
        [name]: value,
        downloadTitle: value.replace(/ /g, '_'),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  return (
    <>
      <PageHeader
        title='Edit a Job Vacancy'
        description="The updated Job Vacancy details will be shown on the Job's page"
        className='mb-3'
      ></PageHeader>

      <FalconComponentCard>
        <FalconComponentCard.Header
          title='Details'
          light={false}
          noPreview={true}
        ></FalconComponentCard.Header>
        <Form onSubmit={handleSubmit}>
          <FalconComponentCard.Body>
            <Row>
              <Col lg='12'>
                <Form.Group>
                  <Form.Label>
                    Title <span className='required-asterisk'>*</span>
                  </Form.Label>
                  <Form.Control
                    size='lg'
                    type='text'
                    placeholder=''
                    name='title'
                    onChange={handleFieldChange}
                    value={formData.title}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg='12'>
                <Form.Group>
                  <Form.Label>
                    Description <span className='required-asterisk'>*</span>
                  </Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={4}
                    size='lg'
                    name='content'
                    onChange={handleFieldChange}
                    value={formData.content}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg='6'>
                <Form.Group>
                  <Form.Label>
                    Closing Date <span className='required-asterisk'>*</span>
                  </Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        maxLength='2'
                        size='lg'
                        type='text'
                        placeholder='DD'
                        name='closingDateDD'
                        onChange={handleFieldChange}
                        value={formData.closingDateDD}
                        required
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        maxLength='2'
                        size='lg'
                        type='text'
                        placeholder='MM'
                        name='closingDateMM'
                        onChange={handleFieldChange}
                        value={formData.closingDateMM}
                        required
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        maxLength='4'
                        size='lg'
                        type='text'
                        placeholder='YYYY'
                        name='closingDateYYYY'
                        onChange={handleFieldChange}
                        value={formData.closingDateYYYY}
                        required
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
              <Col lg='6'>
                <Form.Group>
                  <Form.Label>
                    Reference <span className='required-asterisk'>*</span>
                  </Form.Label>
                  <Form.Control
                    size='lg'
                    type='text'
                    placeholder=''
                    name='reference'
                    onChange={handleFieldChange}
                    value={formData.reference}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg='6'>
                <Form.Group>
                  <Form.Label>
                    Contact Person <span className='required-asterisk'>*</span>
                  </Form.Label>
                  <Form.Control
                    size='lg'
                    type='text'
                    placeholder=''
                    name='contact'
                    onChange={handleFieldChange}
                    value={formData.contact}
                    required
                  />
                </Form.Group>
              </Col>
              <Col lg='6'>
                <Form.Group>
                  <Form.Label>
                    Telephone <span className='required-asterisk'>*</span>
                  </Form.Label>
                  <Form.Control
                    size='lg'
                    type='text'
                    placeholder=''
                    name='tel'
                    onChange={handleFieldChange}
                    value={formData.tel}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg='6'>
                <Form.Group>
                  <Form.Label>
                    Email <span className='required-asterisk'>*</span>
                  </Form.Label>
                  <Form.Control
                    size='lg'
                    type='email'
                    placeholder=''
                    name='email'
                    onChange={handleFieldChange}
                    value={formData.email}
                    required
                  />
                </Form.Group>
              </Col>
              <Col lg='6'>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    size='lg'
                    name='status'
                    onChange={handleFieldChange}
                    value={formData.status}
                  >
                    <option value='True'>Active</option>
                    <option value='False'>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg='12'>
                <Form.Group>
                  <Form.Label>Download File</Form.Label>
                  <br />
                  {formData.downloadFile && (
                    <>
                      <Link to={formData.downloadFile} target='_blank'>
                        View current file
                      </Link>
                      <br />
                      <br />
                    </>
                  )}
                  <FileUpload category={'vacancies'} setFileUrl={setFileUrl} />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col lg='6' className='mt-4 mb-3 ml-3'>
                <Form.Group>
                  <IconButton
                    className='mb-1'
                    variant='falcon-default'
                    size='lg'
                    icon='plus'
                    transform='shrink-3'
                    type='submit'
                  >
                    Update Vacancy
                  </IconButton>
                </Form.Group>
              </Col>
            </Row>
          </FalconComponentCard.Body>
        </Form>
      </FalconComponentCard>
    </>
  );
};

export default EditVacancy;
